import { useContext, useEffect, useState, useCallback } from 'react';
import { IconBadgeListItem } from '@europrocurement/flexy-components/components/molecules/IconBadgeList';
import { WebsocketContext } from '@europrocurement/l2d-websocket';
import { CustomAvatarProps, MessageItem, useAvatarUrl } from '@europrocurement/flexy-components';
import { useUser } from '../modules/Utilisateur/hooks';

const useMessagerieNotification = () => {
    const {
        createSocket,
        socketOpen,
        getUnreadMessages,
        getUnreadNotifications,
        messagesUnread,
        notificationsUnread,
    } = useContext(WebsocketContext);

    const [messageItems, setMessageItems] = useState<
        (IconBadgeListItem & { userIdentifier: string })[]
    >([]);
    const [notificationItems, setNotificationItems] = useState<IconBadgeListItem[]>([]);
    const { usersAvatarInfos } = useAvatarUrl();
    const { fetchUser, cache } = useUser();

    /**
     * Format WS item → IconBadgeListItem
     */
    const formatItem = useCallback(
        (itemWS: MessageItem): IconBadgeListItem & { userIdentifier: string } => {
            const { data, messageId } = itemWS;
            const detail = data.message;

            let title = 'Anonyme';
            let message = '';
            let avatar: CustomAvatarProps | undefined;
            let link: string | undefined;

            const redirect = data.discriminant?.idDossierFormalite
                ? `/dossiers/${data.discriminant.idDossierFormalite}?dossier-detail=message`
                : '';

            switch (detail.type) {
                case 'message':
                    message = detail.message;
                    break;

                case 'document':
                    message = `Un document vous a été envoyé : ${detail.dosDocFileName}`;
                    link = detail.dosDocFileUrl;
                    break;

                case 'emoji':
                    message = `a réagi à votre message : ${detail.icon}`;
                    break;
                default:
                    message = 'Vous avez un nouveau message';
            }

            const normalized = data.date.replace('+00:00Z', 'Z');
            const date = new Date(normalized).toLocaleString();

            // Fetch user si absent du cache
            if (!cache[data.userIdentifier] && cache[data.userIdentifier] !== null) {
                fetchUser(data.userIdentifier);
            }

            const user = cache[data.userIdentifier];
            if (user !== null && user !== undefined) {
                title = `${user.firstName} ${user.lastName}`;
                avatar = {
                    firstName: user.firstName ?? '',
                    lastName: user.lastName ?? '',
                    src: usersAvatarInfos?.find((el) => el.avatarUserIdentifier === user.username)
                        ?.currentUserAvatarUrl,
                    keycloakId: user.username,
                };
            }

            return {
                id: messageId,
                title,
                message,
                avatar,
                link,
                date,
                redirect,
                userIdentifier: data.userIdentifier,
            } as IconBadgeListItem & { userIdentifier: string };
        },
        [cache, fetchUser, usersAvatarInfos],
    );

    /**
     * Init socket + unread fetch
     */
    useEffect(() => {
        if (!socketOpen) {
            createSocket();
        } else {
            getUnreadMessages();
            getUnreadNotifications();
        }
    }, [socketOpen, createSocket, getUnreadMessages, getUnreadNotifications]);

    /**
     * Messages unread
     */
    useEffect(() => {
        if (messagesUnread?.length) {
            setMessageItems(messagesUnread.map(formatItem));
        }
    }, [messagesUnread, formatItem]);

    /**
     * Notifications unread
     */
    useEffect(() => {
        if (notificationsUnread?.length) {
            setNotificationItems(notificationsUnread.map(formatItem));
        }
    }, [notificationsUnread, formatItem]);

    /**
     * Update avatars once cache is filled
     */
    useEffect(() => {
        if (!cache || messageItems.length === 0) return;

        let hasUpdate = false;

        const updated = messageItems.map((item) => {
            if (item.avatar) return item;

            const user = cache[item.userIdentifier];
            console.log(user);
            if (!user) return item;

            hasUpdate = true;

            return {
                ...item,
                title: `${user?.firstName} ${user?.lastName}`,
                avatar: {
                    firstName: user.firstName ?? 'Anonyme',
                    lastName: user.lastName ?? '',
                    src: usersAvatarInfos?.find((el) => el.avatarUserIdentifier === user.username)
                        ?.currentUserAvatarUrl,
                    keycloakId: user.username,
                },
            };
        });

        if (hasUpdate) {
            setMessageItems(updated);
        }
    }, [cache, messageItems, usersAvatarInfos]);

    return {
        messageItems,
        notificationItems,
    };
};

export default useMessagerieNotification;
